import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { InvoiceDA } from '../../services/admin/invoice.service';
import { Company } from '../../models/company.model';
import { Error } from '../../models/error.model';
import { Invoice } from '../../models/invoice.model';
import { GlobalSwal } from '../../styles/swal.style';
import { RequirementContent } from '../../models/requirement.content.model';
import { Settings } from '../../models/settings.model';
import { AccountingAccount } from '../../models/accounting.account.model';
import { Service } from '../../models/service.model';
import { Product } from '../../models/product.model';
import { modalHide } from '../../utils/modal.util';
import { getError } from '../../languages/translations/response';
import { accountingAccountRebate } from '../../scripts/item.account.script';
import { errorWebHandler } from '../../scripts/error.web.handler.script';
import { taxSaleZeroCodes } from '../../libraries/tax.code.library';
import { BankStatementDA } from '../../services/admin/bank.statement.service';

import AppLoading from '../Loading';
import AppSaleFormEntries from '../../interfaces/main/sale/component/SaleFormEntries';

export interface AppModalInvoiceUpdateValidateProps {
  companyForUser: Company | null,
  settingsForCompany: Settings | null,
  accountingAccountsForCompany: AccountingAccount[] | null,
  accountingIncomeAccountsForCompany: AccountingAccount[] | null,
  servicesForCompany: Service[] | null,
  productsForCompany: Product[] | null,
  invoice: Invoice | null,
  setInvoice: Dispatch<SetStateAction<Invoice | null>>,
  bankReconContext?: {
    id_bank_statement: string;
    id_bank_statement_line: string;
    bank_date: string;
    bank_amount: number;
    id_treasury_account: string;
  } | null,
  onReconciled?: () => void,
  mode?: 'validation' | 'correction' | 'collect',
  onCollect?: (contentAccounts: {id_content: string, id_account_debit: string, id_account_credit: string}[]) => void,
};

let errorResponse: Error, invoiceResponse: Invoice;

const AppModalInvoiceUpdateValidate: React.FunctionComponent<AppModalInvoiceUpdateValidateProps> = ({companyForUser, settingsForCompany, accountingAccountsForCompany, accountingIncomeAccountsForCompany, servicesForCompany, productsForCompany, invoice, setInvoice, bankReconContext, onReconciled, mode = 'validation', onCollect}) => {
  const {lang} = StateLanguage()
  const isCollect = mode === 'collect'
  const isCorrection = mode === 'correction'
  const modalId = isCollect ? 'modal-invoice-collect-accounts' : isCorrection ? 'modal-invoice-correct-accounts' : 'modal-invoice-update-validate'

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [contents, setContents] = useState({value: [] as {content: RequirementContent, debit: {accounting_account: string, amount: number}, credit: {accounting_account: string, amount: number}}[], valid: [] as boolean[]})
  const [amounts, setAmounts] = useState({value: {base: 0, tax: 0, rebate: 0, content: 0, debit: 0, credit: 0}, valid: false})

  const submitInvoiceValidate = async () => {
    setLoadIndicator('on')

    let contentsIsValid: boolean = contents.valid.every((item) => (item === true))

    if (companyForUser && invoice && contentsIsValid && amounts.valid) {
      let contentAccountsList = contents.value.map((item) => ({id_content: item.content.id, id_account_debit: item.debit.accounting_account, id_account_credit: item.credit.accounting_account}))

      if (isCollect && onCollect) {
        onCollect(contentAccountsList)
        executeHideModalInvoiceUpdateValidate()
        setLoadIndicator('off')
        return
      }

      if (isCorrection) {
        const correctionCompany = {subject_tax: settingsForCompany?.subject_tax ?? companyForUser?.subject_tax ?? false, tax_method: settingsForCompany?.tax_method ?? companyForUser?.tax_method ?? 'effective', tax_declaration: settingsForCompany?.tax_declaration ?? companyForUser?.tax_declaration ?? 'invoicing', tax_period: settingsForCompany?.tax_period ?? companyForUser?.tax_period ?? '', tax_statement: settingsForCompany?.tax_statement ?? companyForUser?.tax_statement ?? '', language: settingsForCompany?.language ?? companyForUser?.language ?? 'FR'}
        await InvoiceDA.patchAccounts(companyForUser.id, invoice.id, contentAccountsList, correctionCompany).then((response) => {
          if (response.status === 200) {
            setInvoice(response.data)
            GlobalSwal.fire({
              title: lang.labels.successfullyCorrectedAccounts,
              text: lang.labels.actionCompletedReturningToPage,
              icon: 'success',
              showConfirmButton: false,
              timer: 2200
            }).then(() => { executeHideModalInvoiceUpdateValidate() })
          } else {
            errorResponse = response.data
            GlobalSwal.fire({
              title: getError(errorResponse.code, lang.code, errorResponse.message),
              text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
              icon: 'error',
              showConfirmButton: false,
              timer: 2200
            })
          }
        }).catch((error) => {
          errorWebHandler(lang, error)
        }).finally(() => {
          setLoadIndicator('off')
        })
        return
      }

      let company = {subject_tax: settingsForCompany?.subject_tax ?? companyForUser?.subject_tax ?? false, tax_method: settingsForCompany?.tax_method ?? companyForUser?.tax_method ?? 'effective', tax_declaration: settingsForCompany?.tax_declaration ?? companyForUser?.tax_declaration ?? 'invoicing', tax_period: settingsForCompany?.tax_period ?? companyForUser?.tax_period ?? '', tax_statement: settingsForCompany?.tax_statement ?? companyForUser?.tax_statement ?? '', language: settingsForCompany?.language ?? companyForUser?.language ?? 'FR'}

      await InvoiceDA.putInvoiceStatus(companyForUser.id, invoice.id, amounts.value.credit, company, contentAccountsList, invoice.foreign_currency || null).then( async (response) => {
        if (response.status === 200) {
          invoiceResponse = response.data
          setInvoice(invoiceResponse)

          if (bankReconContext) {
            await BankStatementDA.payAndLinkInvoice(
              companyForUser!.id,
              bankReconContext.id_bank_statement,
              bankReconContext.id_bank_statement_line,
              {
                id_invoice: invoiceResponse.id,
                kind: 'invoice',
                amount: bankReconContext.bank_amount,
                date: bankReconContext.bank_date,
              }
            )
          }

          GlobalSwal.fire({
            title: lang.labels.successfullyUpdatedStatus,
            text: lang.labels.actionCompletedReturningToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            executeHideModalInvoiceUpdateValidate()
            if (onReconciled) onReconciled()
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            showConfirmButton: false,
            timer: 2200
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        setLoadIndicator('off')
      })
    }
  }

  const executeHideModalInvoiceUpdateValidate = () => {
    modalHide(modalId)
  }

  useEffect( () => {
    if (companyForUser && settingsForCompany && accountingAccountsForCompany && accountingIncomeAccountsForCompany && servicesForCompany && productsForCompany && invoice) {
      let totalAmountBase: number = 0
      let totalAmountTax: number = 0
      let totalAmountRebate: number = 0
      let totalAmountContent: number = 0
      let totalAmountDebit: number = 0
      let totalAmountCredit: number = 0

      let tempContentsValue = invoice.requirements.flatMap((item) => {
        return item.contents.map((sub_item) => {
          let accountContent: string = ''
          let accountRebate: string = ''

          if (isCorrection && sub_item.id_account && sub_item.id_account.length > 0) {
            accountContent = sub_item.id_account
          } else {
            if (item.type === 'product') {
              const defaultProductAccount = accountingIncomeAccountsForCompany.find((other_item) => (other_item.code === '3200'))?.id ?? ''
              if (sub_item.account && sub_item.account.length > 0) {
                accountContent = sub_item.account
              } else if (sub_item.reference && sub_item.reference.length > 0) {
                accountContent = productsForCompany.find((other_item) => (other_item.variants.some((other_sub_item) => (other_sub_item.id === sub_item.reference))))?.accounts?.income || defaultProductAccount
              } else {
                accountContent = defaultProductAccount
              }
            }
            if (item.type === 'service') {
              const defaultServiceAccount = accountingIncomeAccountsForCompany.find((other_item) => (other_item.code === '3400'))?.id ?? ''
              if (sub_item.account && sub_item.account.length > 0) {
                accountContent = sub_item.account
              } else if (sub_item.reference && sub_item.reference.length > 0) {
                accountContent = servicesForCompany.find((other_item) => (other_item.id === sub_item.reference))?.accounts?.income || defaultServiceAccount
              } else {
                accountContent = defaultServiceAccount
              }
            }
          }

          accountRebate = ((sub_item.rebate && sub_item.rebate.type !== 'none') || (!sub_item.rebate && (sub_item.rebate_amount ?? 0) > 0) || sub_item.price_unit < 0) ? accountingAccountRebate(accountingAccountsForCompany, accountingIncomeAccountsForCompany, accountContent) : ''

          let amountBase: number = 0
          let amountTax: number = 0
          let amountRebate: number = 0
          let amountContent: number = 0

          const subjectTax = settingsForCompany?.subject_tax ?? companyForUser?.subject_tax ?? false
          const taxMethod = settingsForCompany?.tax_method ?? companyForUser?.tax_method ?? 'effective'
          const taxDeclaration = settingsForCompany?.tax_declaration ?? companyForUser?.tax_declaration ?? 'invoicing'

          if (subjectTax) {
            if (taxDeclaration === "invoicing") {
              if (taxMethod === "effective") {
                amountBase = sub_item.price_net
                amountTax = sub_item.tax_value ?? sub_item.tax_amount ?? 0
                amountRebate = (sub_item.price_unit < 0) ? ((amountBase - amountTax) * -1) : sub_item.rebate_amount
                amountContent = (sub_item.price_unit > 0) ? (amountBase - amountTax + amountRebate) : 0
              } else {
                amountBase = sub_item.price_net
                amountTax = (!taxSaleZeroCodes().includes(sub_item.tax_code)) ? (amountBase * ((settingsForCompany?.tax_fees ?? []).find((item) => (item.code.toUpperCase() === taxMethod.toUpperCase()))?.rate ?? 0)) : 0
                amountRebate = (sub_item.price_unit < 0) ? (amountBase * -1) : sub_item.rebate_amount
                amountContent = (sub_item.price_unit > 0) ? (amountBase + amountRebate) : 0
              }
            }
            if (taxDeclaration === "cashing") {
              if (taxMethod === "effective") {
                amountBase = sub_item.price_net
                amountTax = sub_item.tax_value ?? sub_item.tax_amount ?? 0
                amountRebate = (sub_item.price_unit < 0) ? ((amountBase - amountTax) * -1) : sub_item.rebate_amount
                amountContent = (sub_item.price_unit > 0) ? (amountBase - amountTax + amountRebate) : 0
              } else {
                amountBase = sub_item.price_net
                amountTax = (!taxSaleZeroCodes().includes(sub_item.tax_code)) ? (amountBase * ((settingsForCompany?.tax_fees ?? []).find((item) => (item.code.toUpperCase() === taxMethod.toUpperCase()))?.rate ?? 0)) : 0
                amountRebate = (sub_item.price_unit < 0) ? ((amountBase) * -1) : sub_item.rebate_amount
                amountContent = (sub_item.price_unit > 0) ? (amountBase + amountRebate) : 0
              }
            }
          } else {
              amountBase = sub_item.price_net
              amountRebate = (sub_item.price_unit < 0) ? (amountBase * -1) : sub_item.rebate_amount
              amountContent = (sub_item.price_unit > 0) ? (amountBase + amountRebate) : 0
          }

          totalAmountBase += amountBase
          totalAmountTax += amountTax
          totalAmountRebate += amountRebate
          totalAmountContent += amountContent

          return {content: sub_item, debit: {accounting_account: accountRebate, amount: amountRebate}, credit: {accounting_account: accountContent, amount: amountContent}}
        })
      })

      let tempContentsValid = tempContentsValue.map((item) => { return item.credit.accounting_account.length > 0 })

      const gSubjectTax = settingsForCompany?.subject_tax ?? companyForUser?.subject_tax ?? false
      const gTaxMethod = settingsForCompany?.tax_method ?? companyForUser?.tax_method ?? 'effective'
      totalAmountDebit = Math.round((totalAmountBase + totalAmountRebate + ((gSubjectTax && (gTaxMethod === 'tdfn' || gTaxMethod === 'taf')) ? totalAmountTax : 0)) * 100) / 100
      totalAmountCredit = Math.round((totalAmountContent + totalAmountTax) * 100) / 100

      setContents({value: tempContentsValue, valid: tempContentsValid})
      setAmounts({value: {base: totalAmountBase, tax: totalAmountTax, rebate: totalAmountRebate, content: totalAmountContent, debit: totalAmountDebit, credit: totalAmountCredit}, valid: totalAmountDebit === totalAmountCredit})
    } else {
      setContents({value: [], valid: []})
      setAmounts({value: {base: 0, tax: 0, rebate: 0, content: 0, debit: 0, credit: 0}, valid: false})
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, settingsForCompany, accountingAccountsForCompany, accountingIncomeAccountsForCompany, servicesForCompany, productsForCompany, invoice])

  return (
    <div id={modalId} className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{isCollect ? (lang.labels.selectAccounts || 'Select accounts') : isCorrection ? lang.labels.correctAccounts : lang.labels.validateInvoice}</h2>
          </div>
          <div className="modal-body">
            { companyForUser && accountingAccountsForCompany && accountingIncomeAccountsForCompany && invoice
              ?
              <div className="form">
                <div className="text-justify fw-bold mb-5">
                  {isCollect ? (lang.labels.selectAccountsForAutoValidation || 'Select the accounting accounts for each line. These will be used for automatic validation of generated invoices.') : isCorrection ? lang.labels.correctingAccountsDescription : lang.labels.validatingInvoiceSystemWillCreateToFollowingAccountingEntries}
                </div>
                <AppSaleFormEntries component={modalId} invoice={invoice} contents={contents} amounts={amounts} setContents={setContents} setAmounts={setAmounts} companyForUserProp={companyForUser} settingsForCompanyProp={settingsForCompany} accountingAccountsForCompanyProp={accountingAccountsForCompany} accountingIncomeAccountsForCompanyProp={accountingIncomeAccountsForCompany}></AppSaleFormEntries>
              </div>
              :
              <AppLoading></AppLoading>
            }
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModalInvoiceUpdateValidate}>{lang.labels.noCancel}</button>
            <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={submitInvoiceValidate}>
              <span className="indicator-label">{isCollect ? (lang.labels.confirm || 'Confirm') : isCorrection ? lang.labels.saveCorrection : lang.labels.yesValidate}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
};

export default AppModalInvoiceUpdateValidate;
